import { h as htm } from 'minidoc-editor';
import { useMemo, useEffect, useRef, useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { ManualDom } from '@components/manual-dom';
import { useAsyncData } from 'client/lib/hooks';
import { dispatchClearError } from '@components/async-form';
import { useCurrentTenant } from '@components/router/session-context';
import { reportCheckoutError } from './report-checkout-error';
import {
  loadStripe,
  Stripe,
  StripeElements,
  StripePaymentElement,
  StripeElementsOptionsMode,
  Appearance,
} from '@stripe/stripe-js';

export type StripeJs = Stripe;

type StripeLoadResult = { stripe: Stripe; error?: undefined } | { stripe?: undefined; error: true };

const LOAD_FAILED_MESSAGE = `Payment form didn't load. Refresh the page to try again.`;

export type StripeContext = {
  stripe: Stripe;
  elements: StripeElements;
};

type Props = {
  priceId: string;
  customerName: string;
  onReady(c: StripeContext): void;
  collectAddress: boolean;
  disabled?: boolean;
  onAddressChange?(address: { country?: string; postal_code?: string; state?: string }): void;
  /**
   * Prefills the Address Element (when collectAddress is true) with an address
   * already on file — e.g. the card-change form seeding from the customer's
   * saved Stripe address so they confirm rather than retype it.
   */
  defaultAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  amount?: number;
  currency?: string;
  mode?: 'payment' | 'subscription' | 'setup';
  /**
   * Set when the caller turns the entered card into a payment method itself
   * via stripe.createPaymentMethod (e.g. the upsell card form) instead of
   * confirming a server-created intent. Stripe requires the Elements instance
   * to opt in via paymentMethodCreation: 'manual' for that call to work.
   */
  manualPaymentMethod?: boolean;
};

export function StripeInput({
  customerName,
  priceId,
  onReady,
  collectAddress,
  disabled,
  onAddressChange,
  defaultAddress,
  amount,
  currency,
  mode,
  manualPaymentMethod,
}: Props) {
  const tenant = useCurrentTenant();
  const [ready, setReady] = useState(false);
  const [elementLoadFailed, setElementLoadFailed] = useState(false);
  const paymentWrapper = useMemo(() => htm('div'), []);
  const addrWrapper = useMemo(() => htm('div.mt-4'), []);
  const paymentElementRef = useRef<StripePaymentElement | undefined>(undefined);
  const elementsRef = useRef<StripeElements | undefined>(undefined);
  const mountGeneration = useRef(0);

  const cfg = useAsyncData(async (): Promise<StripeLoadResult> => {
    const generation = ++mountGeneration.current;
    setElementLoadFailed(false);
    try {
      return { stripe: await mountStripe(generation) };
    } catch (err) {
      reportCheckoutError({ step: 'load', priceId, err });
      return { error: true };
    }
  }, [priceId]);

  async function mountStripe(generation: number) {
    const config = await rpx.payments.getStripeClientConfig({ priceId });
    const stripe = await loadStripe(config.stripePublishableKey!, {
      stripeAccount: config.stripeAccount,
      locale: tenant.locale,
    });
    if (!stripe) {
      throw new Error(`Failed to load Stripe.`);
    }

    const elementsMode = mode || 'payment';
    const currencyLc = (currency || 'USD').toLowerCase();
    const appearance: Appearance = {
      theme: 'flat',
      variables: {
        colorBackground: '#fff',
        borderRadius: '0.25rem',
        focusBoxShadow: 'none',
        focusOutline: '2px solid #4f46e5',
      },
      rules: {
        '.Input': {
          border: '1px solid #e2e8f0',
          padding: '0.5rem',
        },
        '.Input--invalid': {
          color: '#ef4444',
        },
      },
    };
    const manualPm = manualPaymentMethod
      ? ({ paymentMethodCreation: 'manual' } as const)
      : undefined;
    // Free-trial (setup) path: the server creates a trialing subscription whose
    // pending_setup_intent gets an explicit payment_method_types list — it does
    // not inherit the account's automatic payment methods, and neither
    // Subscription.payment_settings nor SetupIntent update can flip it to
    // automatic. A deferred-mode Element defaults to automatic payment methods,
    // so stripe.confirmSetup would reject the automatic-vs-explicit mismatch.
    // Pin to ['card'] so both sides agree (server pins payment_method_types too).
    const elementsOptions: StripeElementsOptionsMode =
      elementsMode === 'setup'
        ? {
            mode: 'setup',
            currency: currencyLc,
            paymentMethodTypes: ['card'],
            appearance,
            ...manualPm,
          }
        : elementsMode === 'subscription'
          ? {
              mode: 'subscription',
              amount: amount ?? 100,
              currency: currencyLc,
              appearance,
              ...manualPm,
            }
          : {
              mode: 'payment',
              amount: amount ?? 100,
              currency: currencyLc,
              setupFutureUsage: 'off_session',
              appearance,
              ...manualPm,
            };
    const elements = stripe.elements(elementsOptions);

    elementsRef.current = elements;

    // Hide billing details from the Payment Element. Name is collected in our
    // checkout form and passed via confirmParams.billing_details. Address is
    // collected via a separate Stripe Address Element when collectAddress is true.
    const paymentEl = elements.create('payment', {
      layout: 'tabs',
      fields: {
        billingDetails: {
          address: 'never',
          name: 'never',
        },
      },
    });

    paymentElementRef.current = paymentEl;

    paymentEl.on('ready', () => {
      onReady({ stripe, elements });
      requestAnimationFrame(() => setReady(true));
    });
    paymentEl.on('loaderror', (event) => {
      if (generation !== mountGeneration.current) {
        return;
      }
      setElementLoadFailed(true);
      reportCheckoutError({ step: 'load', priceId, err: event.error });
    });
    paymentEl.on('change', () => dispatchClearError(paymentWrapper, 'paymentMethod'));
    paymentEl.mount(paymentWrapper);

    if (collectAddress) {
      const addressDefaults = defaultAddress?.country
        ? {
            line1: defaultAddress.line1,
            line2: defaultAddress.line2,
            city: defaultAddress.city,
            state: defaultAddress.state,
            postal_code: defaultAddress.postal_code,
            country: defaultAddress.country,
          }
        : undefined;
      const addrEl = elements.create('address', {
        mode: 'billing',
        defaultValues: {
          name: customerName,
          address: addressDefaults,
        },
      });
      addrEl.on('change', (event) => {
        if (event.complete && onAddressChange) {
          const addr = event.value?.address;
          onAddressChange({
            country: addr?.country,
            postal_code: addr?.postal_code,
            state: addr?.state,
          });
        }
      });
      addrEl.mount(addrWrapper);
    }

    return stripe;
  }

  const stripe = cfg.data?.stripe;
  const hasLoadError = !cfg.isLoading && (!!cfg.data?.error || elementLoadFailed);

  useEffect(() => {
    if (!stripe) {
      setReady(false);
    }
  }, [stripe]);

  useEffect(() => {
    if (elementsRef.current && stripe && amount !== undefined && mode !== 'setup') {
      elementsRef.current.update({ amount });
    }
  }, [amount, stripe]);

  return (
    <section class="mb-1 relative" data-private>
      {hasLoadError && <p class="py-6 text-center text-sm text-red-600">{LOAD_FAILED_MESSAGE}</p>}
      {!ready && !hasLoadError && (
        <div class="flex items-center justify-center gap-2 py-6 text-gray-400">
          <div class="w-4 h-4 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
          <span class="text-sm">Loading payment form...</span>
        </div>
      )}
      <div
        style={{
          opacity: ready ? 1 : 0,
          transform: ready ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
        }}
      >
        {!!stripe && <ManualDom el={paymentWrapper} />}
        {!!stripe && collectAddress && <ManualDom el={addrWrapper} />}
      </div>
      {disabled && !!stripe && (
        <div class="absolute inset-0 bg-white bg-opacity-95 rounded-sm flex items-center justify-center">
          <div class="flex items-center gap-3 text-gray-700">
            <div class="w-5 h-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
            <span class="font-medium">Processing payment...</span>
          </div>
        </div>
      )}
    </section>
  );
}
